// src/services/firebase.js
//
// Two Firebase apps:
//   1. The app's own project (ddd-profound-philosophy) — the legacy Firestore
//      handle. Per-user data no longer lives here; the hub thin layer
//      (hubAppState.js) is the cloud store.
//   2. The DDD auth hub (ddd-auth-hub-4ed14) — the sign-in session. Login
//      rides the hub's SSO pages; the hub session authenticates the
//      getAppState/saveAppState callables and the Knowledge Hub coaching rail.
import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { getFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

const authHubConfig = {
  apiKey: import.meta.env.VITE_AUTH_HUB_API_KEY,
  authDomain: 'ddd-auth-hub-4ed14.firebaseapp.com',
  projectId: 'ddd-auth-hub-4ed14',
};

export const useAuthHub = import.meta.env.VITE_USE_AUTH_HUB === 'true';

/**
 * Native-safe Auth construction.
 *
 * getAuth() installs the browser popup/redirect resolver, which loads the gapi
 * iframe from the auth domain. That iframe checks its parent origin; under
 * Capacitor the origin is https://localhost (or capacitor://localhost), which
 * gapi rejects — so the iframe never initialises, Auth's internal init promise
 * never settles, and everything awaiting it (signInWithCustomToken above all)
 * hangs forever with NO error. The symptom is a sign-in spinner that never
 * resolves, which is indistinguishable from a network problem.
 *
 * On native we therefore call initializeAuth WITHOUT a popupRedirectResolver,
 * so the iframe is never requested. Nothing is lost: native sign-in goes
 * through the Auth Hub in the in-app browser and returns by deep link, and
 * popup/redirect sign-in is web-only here.
 *
 * Pemberton lost days to this before diagnosing it — see
 * apps/APP_SUBMISSION_PLAYBOOK.md, "Native auth".
 */
const createAuth = (firebaseApp) => {
  if (!Capacitor.isNativePlatform()) {
    return getAuth(firebaseApp);
  }
  return initializeAuth(firebaseApp, {
    persistence: [indexedDBLocalPersistence, browserLocalPersistence],
  });
};

// Initialize Firebase - check if already initialized to prevent duplicate app error
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

// The hub app initialises independently of the own-project config, so hub
// SSO and hub sync survive a build where only the hub key is present.
let authHubApp = null;
let authHubAuth = null;
if (useAuthHub && authHubConfig.apiKey) {
  try {
    authHubApp = getApps().find((a) => a.name === 'auth-hub')
      || initializeApp(authHubConfig, 'auth-hub');
    authHubAuth = createAuth(authHubApp);
  } catch (error) {
    console.error('Auth hub initialisation error:', error);
  }
}
export { authHubApp, authHubAuth };

// Initialize services
export const auth = createAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app);

// Analytics does NOT live here. It rides the fleet's consent-aware GA4 rail
// in src/services/analytics.js (@ddd/analytics), which loads nothing until the
// user opts in on the Data & Privacy page. Firebase's getAnalytics() fires the
// moment it is called, so it cannot honour that promise — and running both
// would double-count every event against the same G-VEP2FEQQL4 stream.

// Enable offline persistence for Firestore - DISABLED for Auth Hub integration
// import { enableIndexedDbPersistence } from 'firebase/firestore';

// enableIndexedDbPersistence(db).catch((err) => {
//   if (err.code === 'failed-precondition') {
//     console.warn('Multiple tabs open, persistence can only be enabled in one tab at a time.');
//   } else if (err.code === 'unimplemented') {
//     console.warn('The current browser does not support offline persistence');
//   }
// });

export default app;